import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, useIsAdmin } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, ShieldCheck, ShieldOff, Key, Copy, RefreshCw, AlertTriangle,
  Cloud, CloudOff, Upload, Clock, Calendar, CheckCircle, Loader2, HardDrive, ExternalLink, Users,
  Settings as SettingsIcon, Download, ToggleLeft, ToggleRight, Palette, Image, Type, Save, Mail, Trash2
} from 'lucide-react';
import UsersRoles from '../components/UsersRoles';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const isAdmin = useIsAdmin();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('security'); // 'security', 'backup', 'users', 'system'
  
  // 2FA Setup states
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [setupStep, setSetupStep] = useState(1);
  
  // Disable 2FA states
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  
  // Backup codes states
  const [isBackupDialogOpen, setIsBackupDialogOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');

  // Google Drive states
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [showBackupsList, setShowBackupsList] = useState(false);
  const [backupsList, setBackupsList] = useState([]);

  // System states (NEW)
  const [appMode, setAppMode] = useState('live');
  const [financialYear, setFinancialYear] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);

  // Branding states (NEW)
  const [brandingData, setBrandingData] = useState({
    company_name: 'Nirala Ennem Enterprises',
    system_name: 'GST Invoice & Inventory Management System',
    logo_url: '/assets/logo.png',
    footer_left: '© 2026 Nirala Ennem Enterprises',
    footer_right: 'GST Invoice & Inventory Management System'
  });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Email settings states (NEW)
  const [emailSettings, setEmailSettings] = useState({
    resend_api_key: '',
    sender_email: 'onboarding@resend.dev',
    sender_name: 'Nirala Ennem ERP'
  });
  const [emailLoading, setEmailLoading] = useState(false);

  // Signature settings states (NEW)
  const [signatureData, setSignatureData] = useState({
    signature_url: null,
    is_default: true
  });
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);

  // Data cleanup states
  const [dataStats, setDataStats] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');

  // Check for OAuth callback result
  useEffect(() => {
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      fetchDriveStatus();
    } else if (searchParams.get('drive_error')) {
      toast.error(`Failed to connect: ${searchParams.get('drive_error')}`);
    }
  }, [searchParams]);

  // Fetch Drive status on mount
  useEffect(() => {
    fetchDriveStatus();
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const [modeRes, fyRes, backupRes, brandingRes, emailRes, signatureRes] = await Promise.all([
        axios.get(`${API_URL}/settings/app-mode`).catch(() => ({ data: { mode: 'live' } })),
        axios.get(`${API_URL}/settings/financial-year`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/settings/backup-status`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/settings/branding`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/settings/email`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/settings/signature`).catch(() => ({ data: null }))
      ]);
      setAppMode(modeRes.data.mode);
      setFinancialYear(fyRes.data);
      setBackupStatus(backupRes.data);
      if (brandingRes.data) {
        setBrandingData(brandingRes.data);
      }
      if (emailRes.data) {
        setEmailSettings(emailRes.data);
      }
      if (signatureRes.data) {
        setSignatureData(signatureRes.data);
      }
      
      // Fetch data stats for cleanup
      const statsRes = await axios.get(`${API_URL}/settings/data-stats`).catch(() => ({ data: null }));
      if (statsRes.data) {
        setDataStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  const handleToggleAppMode = async () => {
    const newMode = appMode === 'live' ? 'test' : 'live';
    const confirmMsg = newMode === 'test' 
      ? 'Switch to TEST MODE? Invoice/PO numbers will be prefixed with TEST-.'
      : 'Switch to LIVE MODE? This will use production invoice/PO numbering.';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.put(`${API_URL}/settings/app-mode?mode=${newMode}`);
      setAppMode(newMode);
      toast.success(`Switched to ${newMode.toUpperCase()} mode`);
    } catch (error) {
      toast.error('Failed to change app mode');
    }
  };

  const handleDownloadLocalBackup = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/download-local-backup`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nirala_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup downloaded');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  // Branding handlers
  const handleBrandingChange = (field, value) => {
    setBrandingData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file must be less than 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    
    setBrandingLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      const response = await axios.post(`${API_URL}/settings/branding/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBrandingData(prev => ({ ...prev, logo_url: response.data.logo_url }));
      setLogoFile(null);
      setLogoPreview(null);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    setBrandingLoading(true);
    try {
      await axios.put(`${API_URL}/settings/branding`, brandingData);
      toast.success('Branding settings saved');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setBrandingLoading(false);
    }
  };

  // Signature handlers
  const handleSignatureSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignatureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadSignature = async () => {
    if (!signatureFile) return;
    
    setSignatureLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', signatureFile);
      
      const response = await axios.post(`${API_URL}/settings/signature/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSignatureData({ signature_url: response.data.signature_url, is_default: false });
      setSignatureFile(null);
      setSignaturePreview(null);
      toast.success('Signature uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload signature');
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleResetSignature = async () => {
    if (!window.confirm('Reset to default signature? The custom signature will be deleted.')) return;
    
    setSignatureLoading(true);
    try {
      await axios.delete(`${API_URL}/settings/signature`);
      setSignatureData({ signature_url: `${API_URL}/settings/signature/default`, is_default: true });
      setSignatureFile(null);
      setSignaturePreview(null);
      toast.success('Signature reset to default');
    } catch (error) {
      toast.error('Failed to reset signature');
    } finally {
      setSignatureLoading(false);
    }
  };

  // Data cleanup handlers
  const handleCleanupQuotations = async (deleteDraft = true, deleteConverted = false) => {
    const msg = deleteDraft && deleteConverted 
      ? 'Delete ALL quotations (draft + converted)?' 
      : deleteDraft 
        ? 'Delete all DRAFT quotations?' 
        : 'Delete all CONVERTED quotations?';
    
    if (!window.confirm(msg + ' This cannot be undone!')) return;
    
    setCleanupLoading(true);
    try {
      const response = await axios.post(`${API_URL}/settings/cleanup/quotations?delete_draft=${deleteDraft}&delete_converted=${deleteConverted}`);
      toast.success(response.data.message);
      fetchSystemSettings(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupPOs = async (deleteDraft = true, deletePending = false) => {
    const msg = deleteDraft && deletePending 
      ? 'Delete all DRAFT and PENDING purchase orders?' 
      : deleteDraft 
        ? 'Delete all DRAFT purchase orders?' 
        : 'Delete all PENDING purchase orders?';
    
    if (!window.confirm(msg + ' This cannot be undone!')) return;
    
    setCleanupLoading(true);
    try {
      const response = await axios.post(`${API_URL}/settings/cleanup/purchase-orders?delete_draft=${deleteDraft}&delete_pending=${deletePending}`);
      toast.success(response.data.message);
      fetchSystemSettings(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupAll = async () => {
    if (confirmPhrase !== 'DELETE ALL TEST DATA') {
      toast.error("Please type 'DELETE ALL TEST DATA' to confirm");
      return;
    }
    
    setCleanupLoading(true);
    try {
      const response = await axios.post(`${API_URL}/settings/cleanup/all-test-data?confirm_phrase=${encodeURIComponent(confirmPhrase)}`);
      toast.success(response.data.message);
      setConfirmPhrase('');
      fetchSystemSettings(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Email settings handlers
  const handleEmailChange = (field, value) => {
    setEmailSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEmailSettings = async () => {
    setEmailLoading(true);
    try {
      await axios.put(`${API_URL}/settings/email`, emailSettings);
      toast.success('Email settings saved successfully');
      // Refresh to get masked API key
      const res = await axios.get(`${API_URL}/settings/email`);
      setEmailSettings(res.data);
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setEmailLoading(false);
    }
  };

  const fetchDriveStatus = async () => {
    setDriveLoading(true);
    try {
      const response = await axios.get(`${API_URL}/google-drive/status`);
      setDriveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch drive status:', error);
    } finally {
      setDriveLoading(false);
    }
  };

  const handleConnectDrive = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/google-drive/connect`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google Drive connection');
      setLoading(false);
    }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive? Your backups will remain in Drive.')) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/google-drive/disconnect`);
      toast.success('Google Drive disconnected');
      setDriveStatus({ connected: false });
    } catch (error) {
      toast.error('Failed to disconnect Google Drive');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupNow = async () => {
    setBackupInProgress(true);
    try {
      const response = await axios.post(`${API_URL}/google-drive/backup`);
      toast.success(`Backup created: ${response.data.filename}`);
      fetchDriveStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup failed');
    } finally {
      setBackupInProgress(false);
    }
  };

  const handleViewBackups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/google-drive/backups`);
      setBackupsList(response.data.backups);
      setShowBackupsList(true);
    } catch (error) {
      toast.error('Failed to fetch backups list');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  // 2FA handlers (unchanged)
  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/setup`);
      setSetupData(response.data);
      setSetupStep(1);
      setVerificationCode('');
      setIsSetupDialogOpen(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/2fa/enable`, { code: verificationCode });
      toast.success('2FA enabled successfully!');
      setIsSetupDialogOpen(false);
      setSetupData(null);
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/2fa/disable`, { code: disableCode });
      toast.success('2FA disabled successfully');
      setIsDisableDialogOpen(false);
      setDisableCode('');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBackupCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/auth/2fa/backup-codes`);
      setBackupCodes(response.data.backup_codes);
      setIsBackupDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch backup codes');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/regenerate-backup`, { code: regenerateCode });
      setBackupCodes(response.data.backup_codes);
      toast.success('Backup codes regenerated');
      setIsRegenerateDialogOpen(false);
      setRegenerateCode('');
      setIsBackupDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyAllBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success('All backup codes copied');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your account security, data backups, and user access</p>
      </div>

      {/* Section Tabs (Admin only sees all tabs) */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveSection('security')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeSection === 'security' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Security
        </button>
        <button
          onClick={() => setActiveSection('backup')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeSection === 'backup' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Cloud className="w-4 h-4 inline mr-2" />
          Backup
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveSection('users')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'users' 
                ? 'border-teal-600 text-teal-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Users & Roles
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveSection('system')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'system' 
                ? 'border-teal-600 text-teal-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <SettingsIcon className="w-4 h-4 inline mr-2" />
            System
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveSection('branding')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'branding' 
                ? 'border-teal-600 text-teal-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Palette className="w-4 h-4 inline mr-2" />
            Branding
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveSection('email')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'email' 
                ? 'border-teal-600 text-teal-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Email
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setActiveSection('cleanup')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'cleanup' 
                ? 'border-red-600 text-red-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Trash2 className="w-4 h-4 inline mr-2" />
            Data Cleanup
          </button>
        )}
      </div>

      {/* System Section (Admin Only) */}
      {activeSection === 'system' && isAdmin && (
        <div className="space-y-6">
          {/* App Mode Toggle */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  appMode === 'test' ? 'bg-amber-100' : 'bg-green-100'
                }`}>
                  {appMode === 'test' ? (
                    <ToggleLeft className="w-6 h-6 text-amber-600" />
                  ) : (
                    <ToggleRight className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">Application Mode</CardTitle>
                  <CardDescription>Switch between Test and Live modes</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">
                    Current Mode: <span className={appMode === 'test' ? 'text-amber-600' : 'text-green-600'}>
                      {appMode.toUpperCase()}
                    </span>
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {appMode === 'test' 
                      ? 'Test mode - Safe for testing without affecting real data'
                      : 'Live mode - Production environment for real transactions'}
                  </p>
                </div>
                <Button
                  onClick={handleToggleAppMode}
                  variant={appMode === 'test' ? 'default' : 'outline'}
                  className={appMode === 'test' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  Switch to {appMode === 'test' ? 'LIVE' : 'TEST'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Financial Year Info */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-teal-100">
                  <Calendar className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Financial Year</CardTitle>
                  <CardDescription>Current financial year for numbering</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {financialYear && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-teal-600">{financialYear.display_name}</p>
                    <p className="text-sm text-slate-500">Current FY</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-lg font-medium">{financialYear.fy_start}</p>
                    <p className="text-sm text-slate-500">Start Date</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-lg font-medium">{financialYear.fy_end}</p>
                    <p className="text-sm text-slate-500">End Date</p>
                  </div>
                </div>
              )}
              <p className="text-sm text-slate-500 mt-4">
                Invoice and PO numbers automatically reset at the start of each financial year (April 1st).
              </p>
            </CardContent>
          </Card>

          {/* Local Backup Download */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100">
                  <Download className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Manual Backup</CardTitle>
                  <CardDescription>Download a local backup of all data</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {backupStatus && backupStatus.last_backup && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Last backup: {new Date(backupStatus.last_backup).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">File: {backupStatus.file_name}</p>
                </div>
              )}
              <Button onClick={handleDownloadLocalBackup} className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Download Local Backup (JSON)
              </Button>
              <p className="text-xs text-slate-500 text-center">
                This downloads all data including products, customers, vendors, purchases, sales, and inventory.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Branding Section (Admin Only) */}
      {activeSection === 'branding' && isAdmin && (
        <div className="space-y-6">
          {/* Logo Upload */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-purple-100">
                  <Image className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Company Logo</CardTitle>
                  <CardDescription>Upload your company logo (max 2MB, PNG/JPG/SVG)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-6">
                {/* Current Logo Preview */}
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-slate-700 mb-2">Current Logo</p>
                  <div className="w-32 h-32 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                    <img 
                      src={logoPreview || brandingData.logo_url} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => { e.target.src = '/assets/logo.png'; }}
                    />
                  </div>
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1 space-y-3">
                  <div>
                    <Label htmlFor="logo-upload" className="text-sm font-medium text-slate-700">Upload New Logo</Label>
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Recommended: Square image (200x200px or larger), PNG with transparent background
                    </p>
                  </div>
                  
                  {logoFile && (
                    <Button 
                      onClick={handleUploadLogo} 
                      disabled={brandingLoading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {brandingLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Logo
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signature Upload */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-indigo-100">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                <div>
                  <CardTitle className="text-lg">Authorized Signature</CardTitle>
                  <CardDescription>Upload signature for PDF documents (PO, Invoices). Max 1MB, PNG recommended</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-6">
                {/* Current Signature Preview */}
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Current Signature {signatureData.is_default && <span className="text-xs text-slate-500">(Default)</span>}
                  </p>
                  <div className="w-40 h-24 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-white overflow-hidden">
                    <img 
                      src={signaturePreview || signatureData.signature_url || `${API_URL}/settings/signature/default`} 
                      alt="Signature preview" 
                      className="max-w-full max-h-full object-contain"
                      style={{ filter: 'invert(0)' }}
                      onError={(e) => { e.target.src = `${API_URL}/settings/signature/default`; }}
                    />
                  </div>
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1 space-y-3">
                  <div>
                    <Label htmlFor="signature-upload" className="text-sm font-medium text-slate-700">Upload New Signature</Label>
                    <Input
                      id="signature-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureSelect}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Recommended: PNG with transparent background, or white background with dark signature
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    {signatureFile && (
                      <Button 
                        onClick={handleUploadSignature} 
                        disabled={signatureLoading}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {signatureLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload Signature
                      </Button>
                    )}
                    
                    {!signatureData.is_default && (
                      <Button 
                        onClick={handleResetSignature} 
                        disabled={signatureLoading}
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Reset to Default
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-teal-100">
                  <Type className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Company Information</CardTitle>
                  <CardDescription>Customize your company name and system branding</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-sm font-medium text-slate-700">Company Name</Label>
                  <Input
                    id="company_name"
                    value={brandingData.company_name}
                    onChange={(e) => handleBrandingChange('company_name', e.target.value)}
                    placeholder="Your Company Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="system_name" className="text-sm font-medium text-slate-700">System Name</Label>
                  <Input
                    id="system_name"
                    value={brandingData.system_name}
                    onChange={(e) => handleBrandingChange('system_name', e.target.value)}
                    placeholder="System Title"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer Text */}
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100">
                  <Palette className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Footer Branding</CardTitle>
                  <CardDescription>Customize the footer text shown across all pages</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="footer_left" className="text-sm font-medium text-slate-700">Footer Left Text</Label>
                  <Input
                    id="footer_left"
                    value={brandingData.footer_left}
                    onChange={(e) => handleBrandingChange('footer_left', e.target.value)}
                    placeholder="© 2026 Company Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="footer_right" className="text-sm font-medium text-slate-700">Footer Right Text</Label>
                  <Input
                    id="footer_right"
                    value={brandingData.footer_right}
                    onChange={(e) => handleBrandingChange('footer_right', e.target.value)}
                    placeholder="System Description"
                  />
                </div>
              </div>
              
              {/* Preview */}
              <div className="mt-4 p-4 bg-[#f8f9fa] border border-slate-200 rounded-lg">
                <p className="text-xs text-slate-500 mb-2 font-medium">Footer Preview:</p>
                <div className="flex justify-between items-center text-xs text-slate-500">
                  <span>{brandingData.footer_left}</span>
                  <span className="hidden sm:inline text-slate-300">|</span>
                  <span>{brandingData.footer_right}</span>
                </div>
              </div>
              
              <Button 
                onClick={handleSaveBranding} 
                disabled={brandingLoading}
                className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
              >
                {brandingLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Branding Settings
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Configuration Section (Admin Only) */}
      {activeSection === 'email' && isAdmin && (
        <div className="space-y-6">
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Email Service Configuration</CardTitle>
                  <CardDescription>Configure Resend API for sending emails (e.g., Send to Accountant)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Setup Instructions:</strong> Sign up at{' '}
                  <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                    resend.com
                  </a>
                  , then create an API key from Dashboard → API Keys. The key starts with <code className="bg-blue-100 px-1 rounded">re_</code>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="resend_api_key" className="text-sm font-medium text-slate-700">
                  Resend API Key *
                </Label>
                <Input
                  id="resend_api_key"
                  type="password"
                  value={emailSettings.resend_api_key}
                  onChange={(e) => handleEmailChange('resend_api_key', e.target.value)}
                  placeholder="re_xxxxxxxx..."
                  data-testid="resend-api-key-input"
                />
                <p className="text-xs text-slate-500">
                  {emailSettings.resend_api_key && emailSettings.resend_api_key.includes('...') 
                    ? 'Key is saved and masked for security' 
                    : 'Enter your Resend API key'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_email" className="text-sm font-medium text-slate-700">
                    Sender Email
                  </Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={emailSettings.sender_email}
                    onChange={(e) => handleEmailChange('sender_email', e.target.value)}
                    placeholder="onboarding@resend.dev"
                    data-testid="sender-email-input"
                  />
                  <p className="text-xs text-slate-500">
                    Use <code className="bg-slate-100 px-1 rounded">onboarding@resend.dev</code> for testing
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sender_name" className="text-sm font-medium text-slate-700">
                    Sender Name
                  </Label>
                  <Input
                    id="sender_name"
                    value={emailSettings.sender_name}
                    onChange={(e) => handleEmailChange('sender_name', e.target.value)}
                    placeholder="Nirala Ennem ERP"
                    data-testid="sender-name-input"
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleSaveEmailSettings} 
                disabled={emailLoading}
                className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
                data-testid="save-email-settings-btn"
              >
                {emailLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Email Settings
              </Button>
            </CardContent>
          </Card>
          
          {/* Test Email Note */}
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Testing Mode Note</p>
                  <p className="text-xs text-amber-700 mt-1">
                    In Resend testing mode, emails can only be sent to verified email addresses. 
                    To send to any email, you need to verify a domain in your Resend dashboard.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Data Cleanup Section (Separate Tab) */}
      {activeSection === 'cleanup' && isAdmin && (
        <div className="space-y-6">
          <Card className="border border-red-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-red-700">Data Cleanup</CardTitle>
                  <CardDescription>Remove test data and reset numbering. Use with caution!</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Data Stats */}
              {dataStats && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Quotations</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Draft: <span className="font-medium">{dataStats.quotations.draft}</span> | 
                      Converted: <span className="font-medium">{dataStats.quotations.converted}</span> | 
                      Total: <span className="font-medium">{dataStats.quotations.total}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">Purchase Orders</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Draft: <span className="font-medium">{dataStats.purchase_orders.draft}</span> | 
                      Pending: <span className="font-medium">{dataStats.purchase_orders.pending}</span> | 
                      Received: <span className="font-medium">{dataStats.purchase_orders.received}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Individual Cleanup Options */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">Selective Cleanup</p>
                
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    onClick={() => handleCleanupQuotations(true, false)}
                    disabled={cleanupLoading || !dataStats?.quotations.draft}
                  >
                    {cleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete Draft Quotations ({dataStats?.quotations.draft || 0})
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    onClick={() => handleCleanupPOs(true, false)}
                    disabled={cleanupLoading || !dataStats?.purchase_orders.draft}
                  >
                    {cleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete Draft POs ({dataStats?.purchase_orders.draft || 0})
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    onClick={() => handleCleanupPOs(false, true)}
                    disabled={cleanupLoading || !dataStats?.purchase_orders.pending}
                  >
                    {cleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete Pending POs ({dataStats?.purchase_orders.pending || 0})
                  </Button>
                </div>
              </div>

              {/* Full Cleanup */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium text-red-700">⚠️ Full Test Data Cleanup</p>
                <p className="text-xs text-slate-600">
                  This will delete ALL quotations and all draft/pending purchase orders. 
                  Received POs are preserved. Type <code className="bg-red-100 px-1 rounded text-red-700">DELETE ALL TEST DATA</code> to confirm.
                </p>
                
                <div className="flex gap-2">
                  <Input
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder="Type DELETE ALL TEST DATA"
                    className="max-w-xs border-red-200"
                  />
                  <Button
                    variant="destructive"
                    onClick={handleCleanupAll}
                    disabled={cleanupLoading || confirmPhrase !== 'DELETE ALL TEST DATA'}
                  >
                    {cleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Clean All Test Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users & Roles Section (Admin Only) */}
      {activeSection === 'users' && isAdmin && (
        <UsersRoles />
      )}

      {/* Google Drive Backup Section */}
      {activeSection === 'backup' && (
      <Card className="border border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              driveStatus?.connected ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              {driveStatus?.connected ? (
                <Cloud className="w-6 h-6 text-blue-600" />
              ) : (
                <CloudOff className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg">Google Drive Backup</CardTitle>
              <CardDescription>
                Automatically backup your data to Google Drive
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {driveLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : driveStatus?.connected ? (
            <>
              {/* Connected Status */}
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-700">Google Drive Connected</span>
                </div>
                <p className="text-sm text-slate-600">
                  Your data is automatically backed up daily to the "Nirala ERP Backups" folder.
                </p>
              </div>

              {/* Backup Status Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Last Backup
                  </div>
                  <p className="font-medium text-slate-900">
                    {driveStatus.last_backup ? formatDate(driveStatus.last_backup) : 'No backups yet'}
                  </p>
                  {driveStatus.last_backup_file && (
                    <p className="text-xs text-slate-500 mt-1">{driveStatus.last_backup_file}</p>
                  )}
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Next Scheduled
                  </div>
                  <p className="font-medium text-slate-900">
                    {driveStatus.next_backup ? formatDate(driveStatus.next_backup) : 'Daily at midnight'}
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <HardDrive className="w-4 h-4" />
                    Total Backups
                  </div>
                  <p className="font-medium text-slate-900">
                    {driveStatus.backup_count || 0} / 30
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Older backups auto-deleted</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleBackupNow}
                  disabled={backupInProgress}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="backup-now-btn"
                >
                  {backupInProgress ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Backing up...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Backup Now
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleViewBackups}
                  disabled={loading}
                  data-testid="view-backups-btn"
                >
                  <HardDrive className="w-4 h-4 mr-2" />
                  View Backups
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:border-red-300"
                  onClick={handleDisconnectDrive}
                  disabled={loading}
                  data-testid="disconnect-drive-btn"
                >
                  <CloudOff className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              </div>

              {/* Backup Contents Info */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Backup includes:</p>
                <div className="flex flex-wrap gap-2">
                  {['Products', 'Vendors', 'Customers', 'Purchases', 'Invoices', 'Inventory', 'Credit Notes'].map((item) => (
                    <span key={item} className="px-2 py-1 bg-white rounded border text-xs text-slate-600">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Not Connected Status */}
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-700">Google Drive Not Connected</span>
                </div>
                <p className="text-sm text-slate-600">
                  Connect your Google Drive to enable automatic daily backups of your business data.
                </p>
              </div>

              <Button
                onClick={handleConnectDrive}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="connect-drive-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Cloud className="w-4 h-4 mr-2" />
                    Connect Google Drive
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* 2FA Section */}
      {activeSection === 'security' && (
      <>
      <Card className="border border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              user?.two_factor_enabled ? 'bg-emerald-100' : 'bg-slate-100'
            }`}>
              {user?.two_factor_enabled ? (
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              ) : (
                <Shield className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg">Two-Factor Authentication (2FA)</CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={`p-4 rounded-lg ${
            user?.two_factor_enabled ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
          }`} data-testid="2fa-status-container">
            <div className="flex items-center gap-2">
              {user?.two_factor_enabled ? (
                <>
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-700" data-testid="2fa-enabled-text">2FA is enabled</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-700" data-testid="2fa-disabled-text">2FA is not enabled</span>
                </>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-2">
              {user?.two_factor_enabled 
                ? 'Your account is protected with two-factor authentication.' 
                : 'Enable 2FA to protect your account with an additional verification step.'}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {user?.two_factor_enabled ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleViewBackupCodes}
                  disabled={loading}
                  data-testid="view-backup-codes-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  View Backup Codes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsRegenerateDialogOpen(true)}
                  disabled={loading}
                  data-testid="regenerate-codes-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate Codes
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsDisableDialogOpen(true)}
                  disabled={loading}
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="w-4 h-4 mr-2" />
                  Disable 2FA
                </Button>
              </>
            ) : (
              <Button
                onClick={handleSetup2FA}
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="enable-2fa-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Enable 2FA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compatible Apps Info */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <h3 className="font-medium text-slate-900 mb-2">Compatible Authenticator Apps</h3>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1">✓ Google Authenticator</span>
            <span className="flex items-center gap-1">✓ Microsoft Authenticator</span>
            <span className="flex items-center gap-1">✓ Authy</span>
            <span className="flex items-center gap-1">✓ 1Password</span>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Backups List Dialog */}
      <Dialog open={showBackupsList} onOpenChange={setShowBackupsList}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-blue-600" />
              Backup History
            </DialogTitle>
            <DialogDescription>
              Your recent backups stored in Google Drive
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {backupsList.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No backups found. Click "Backup Now" to create your first backup.
              </div>
            ) : (
              backupsList.map((backup) => (
                <div key={backup.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm text-slate-900">{backup.name}</p>
                    <p className="text-xs text-slate-500">
                      {formatDate(backup.created_at)} • {formatFileSize(backup.size)}
                    </p>
                  </div>
                  {backup.web_link && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(backup.web_link, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Setup 2FA Dialog */}
      <Dialog open={isSetupDialogOpen} onOpenChange={setIsSetupDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-600" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              {setupStep === 1 
                ? 'Scan the QR code with your authenticator app'
                : 'Enter the verification code from your app'}
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4" data-testid="2fa-setup-dialog-content">
              {setupStep === 1 ? (
                <>
                  <div className="flex justify-center p-4 bg-white rounded-lg border" data-testid="qr-code-container">
                    <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this key manually:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono bg-white p-2 rounded border" data-testid="2fa-secret-code">
                        {setupData.secret}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(setupData.secret)}
                        data-testid="copy-secret-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="backup-codes-preview">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-amber-700 text-sm">Recovery Backup Codes</span>
                    </div>
                    <p className="text-xs text-amber-600 mb-2">
                      Save these codes in a safe place. You can use them to login if you lose your phone.
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {setupData.backup_codes.map((code, i) => (
                        <code key={i} className="text-xs font-mono bg-white p-1 rounded text-center">
                          {code}
                        </code>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 w-full"
                      onClick={() => copyToClipboard(setupData.backup_codes.join('\n'))}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy All Codes
                    </Button>
                  </div>
                  
                  <Button onClick={() => setSetupStep(2)} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="scanned-qr-btn">
                    I've scanned the QR code
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Enter 6-digit verification code</Label>
                    <Input
                      type="text"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest font-mono"
                      autoFocus
                      data-testid="setup-verification-code-input"
                    />
                    <p className="text-xs text-slate-500 text-center">
                      Enter the code from your authenticator app
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setSetupStep(1)} className="flex-1" data-testid="setup-back-btn">
                      Back
                    </Button>
                    <Button
                      onClick={handleVerify2FA}
                      disabled={loading || verificationCode.length !== 6}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                      data-testid="verify-enable-2fa-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify & Enable'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={isDisableDialogOpen} onOpenChange={setIsDisableDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldOff className="w-5 h-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your 2FA code to confirm disabling. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Enter 6-digit verification code</Label>
              <Input
                type="text"
                maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
                data-testid="disable-2fa-code-input"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDisableDialogOpen(false)} data-testid="disable-cancel-btn">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisable2FA}
                disabled={loading || disableCode.length !== 6}
                data-testid="disable-confirm-btn"
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Backup Codes Dialog */}
      <Dialog open={isBackupDialogOpen} onOpenChange={setIsBackupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-teal-600" />
              Backup Recovery Codes
            </DialogTitle>
            <DialogDescription>
              Use these codes to login if you lose access to your authenticator app.
              Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-600 mb-2">
                You have {backupCodes.length} backup codes remaining
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded border">
                  <code className="font-mono text-sm">{code}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(code)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            
            {backupCodes.length === 0 && (
              <div className="text-center py-4 text-slate-500">
                No backup codes remaining. Please regenerate.
              </div>
            )}
            
            <Button variant="outline" className="w-full" onClick={copyAllBackupCodes}>
              <Copy className="w-4 h-4 mr-2" />
              Copy All Codes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={isRegenerateDialogOpen} onOpenChange={setIsRegenerateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-teal-600" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription>
              This will invalidate all existing backup codes. Enter your 2FA code to confirm.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Enter 6-digit verification code</Label>
              <Input
                type="text"
                maxLength={6}
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
                data-testid="regenerate-code-input"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRegenerateDialogOpen(false)} data-testid="regenerate-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleRegenerateBackupCodes}
                disabled={loading || regenerateCode.length !== 6}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="regenerate-confirm-btn"
              >
                {loading ? 'Regenerating...' : 'Regenerate Codes'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
